import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const WholesaleInformation = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".animate-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-6">🏢</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Wholesale Information
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Partner with Gift of God Fragrances for exclusive wholesale opportunities and premium fragrance collections.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-6xl mx-auto px-4 py-16">
        {/* Benefits Grid */}
        <div className="animate-section grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">💰</div>
            <h3 className="text-lg font-semibold text-black mb-2">Competitive Pricing</h3>
            <p className="text-gray-600 text-sm">Exclusive wholesale rates with volume discounts</p>
          </div>
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">🚚</div>
            <h3 className="text-lg font-semibold text-black mb-2">Fast Shipping</h3>
            <p className="text-gray-600 text-sm">Priority shipping for wholesale orders</p>
          </div>
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-black mb-2">Dedicated Support</h3>
            <p className="text-gray-600 text-sm">Personal account manager for your business</p>
          </div>
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">🏢</div>
            <h3 className="text-lg font-semibold text-black mb-2">Business Tools</h3>
            <p className="text-gray-600 text-sm">Marketing materials and business resources</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Requirements */}
          <div className="animate-section">
            <div className="bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg h-full">
              <h2 className="text-3xl font-bold text-black mb-6">Wholesale Requirements</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Business License</h4>
                    <p className="text-gray-700 text-sm">Valid business license or reseller permit required</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Minimum Order</h4>
                    <p className="text-gray-700 text-sm">$500 minimum initial order, $250 minimum reorders</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Business Type</h4>
                    <p className="text-gray-700 text-sm">Retail stores, salons, spas, online retailers welcome</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Credit Application</h4>
                    <p className="text-gray-700 text-sm">Complete credit application and business references</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="animate-section">
            <div className="bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20 h-full">
              <h2 className="text-3xl font-bold text-black mb-6">Wholesale Benefits</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Volume Discounts</h4>
                    <p className="text-gray-700 text-sm">Up to 50% off retail prices with tiered pricing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Exclusive Products</h4>
                    <p className="text-gray-700 text-sm">Access to wholesale-only fragrances and limited editions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Marketing Support</h4>
                    <p className="text-gray-700 text-sm">Product images, descriptions, and promotional materials</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#A88006] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="text-[#A88006] font-semibold">Flexible Terms</h4>
                    <p className="text-gray-700 text-sm">Net 30 payment terms for qualified businesses</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="animate-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Wholesale Pricing Tiers</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg text-center">
              <h3 className="text-xl font-bold text-[#A88006] mb-4">Bronze Tier</h3>
              <div className="text-3xl font-bold text-black mb-2">25% OFF</div>
              <p className="text-gray-600 mb-4">Orders $500 - $1,999</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Standard wholesale pricing</li>
                <li>• Email support</li>
                <li>• Basic marketing materials</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#A88006]/20 to-[#9F7800]/20 p-6 rounded-xl border border-[#A88006]/40 text-center relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#A88006] text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <h3 className="text-xl font-bold text-[#A88006] mb-4">Silver Tier</h3>
              <div className="text-3xl font-bold text-black mb-2">35% OFF</div>
              <p className="text-gray-600 mb-4">Orders $2,000 - $4,999</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Enhanced wholesale pricing</li>
                <li>• Priority support</li>
                <li>• Premium marketing kit</li>
                <li>• Net 30 payment terms</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg text-center">
              <h3 className="text-xl font-bold text-[#A88006] mb-4">Gold Tier</h3>
              <div className="text-3xl font-bold text-black mb-2">50% OFF</div>
              <p className="text-gray-600 mb-4">Orders $5,000+</p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Maximum wholesale pricing</li>
                <li>• Dedicated account manager</li>
                <li>• Custom marketing support</li>
                <li>• Exclusive product access</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Application Process */}
        <div className="animate-section mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Application Process</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                1
              </div>
              <h4 className="text-black font-semibold mb-2">Submit Application</h4>
              <p className="text-gray-600 text-sm">Complete our wholesale application form</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                2
              </div>
              <h4 className="text-black font-semibold mb-2">Review Process</h4>
              <p className="text-gray-600 text-sm">We review your application within 2-3 business days</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                3
              </div>
              <h4 className="text-black font-semibold mb-2">Account Setup</h4>
              <p className="text-gray-600 text-sm">Approved accounts receive login credentials</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-lg">
                4
              </div>
              <h4 className="text-black font-semibold mb-2">Start Ordering</h4>
              <p className="text-gray-600 text-sm">Access wholesale portal and place orders</p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="animate-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
          <h2 className="text-3xl font-bold text-black text-center mb-8">Get Started Today</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-[#A88006] mb-4">Ready to Apply?</h3>
              <p className="text-gray-700 mb-6">
                Join our network of successful wholesale partners and start offering premium fragrances to your customers.
              </p>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center gap-3">
                  <span className="text-[#A88006]">📧</span>
                  <span>wholesale@giftofgodfragrances.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#A88006]">📞</span>
                  <span>(848) 667-0850 ext. 2</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#A88006]">🕒</span>
                  <span>Monday - Friday, 9 AM - 6 PM EST</span>
                </div>
              </div>
            </div>
            <div>
              <form className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Business Name"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                  />
                  <input
                    type="text"
                    placeholder="Contact Name"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                />
                <textarea
                  placeholder="Tell us about your business..."
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                ></textarea>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-all"
                >
                  Submit Application
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default WholesaleInformation;